import { tabSessionUUID } from '@vs/utils/constants';
import { useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';

import { useOidcConfig, useSignOut } from '.';
import { BroadcastIds, MessagePayload } from '../types';
import { setIsSessionEndInSameClient } from '../utils/_private';
import { broadcastSameClientLogout } from '../utils/broadcastSameClientLogout';

function isAutoLogoutMsg(msg: unknown): msg is MessagePayload {
  return (
    msg !== null &&
    typeof msg === 'object' &&
    'id' in msg &&
    msg.id === BroadcastIds.MESSAGE &&
    'payload' in msg &&
    msg.payload !== null &&
    typeof msg.payload === 'object' &&
    'client_id' in msg.payload &&
    'authority' in msg.payload &&
    'sessionUUID' in msg.payload
  );
}

export type ArgsUseHandleSameClientLogout = {
  onLogout?: () => void;
};
export function useHandleSameClientLogout({
  onLogout = () => {},
}: ArgsUseHandleSameClientLogout = {}) {
  const cbRef = useRef<
    Required<Pick<ArgsUseHandleSameClientLogout, 'onLogout'>>
  >({
    onLogout: () => {},
  });
  cbRef.current.onLogout = onLogout;

  const { isAuthenticated } = useAuth();
  const { client_id, authority } = useOidcConfig();
  const signOut = useSignOut();

  useEffect(() => {
    if (isAuthenticated) {
      broadcastSameClientLogout({
        sessionUUID: tabSessionUUID,
        authority,
        client_id,
        isLogout: false,
      });
    }
  }, [isAuthenticated, authority, client_id]);
  useEffect(() => {
    const channel = new BroadcastChannel(BroadcastIds.CHANNEL);

    const onMessage = (e: MessageEvent<MessagePayload>) => {
      const msg = e.data;
      if (isAutoLogoutMsg(msg)) {
        const p = msg.payload;
        if (
          p.sessionUUID !== tabSessionUUID &&
          p.authority === authority &&
          p.client_id === client_id
        ) {
          setIsSessionEndInSameClient(p.isLogout ?? false);
          if (p.isLogout) {
            cbRef.current.onLogout();
          }
        }
      }
    };

    channel.addEventListener('message', onMessage);

    return () => {
      channel.removeEventListener('message', onMessage);
      channel.close();
    };
  }, [signOut, client_id, authority]);
}
